import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { groupKeys } from '../groups/groupsApi.ts';
import { describeError } from '../../ui/problemDetails.ts';
import {
  acceptInvitation,
  declineInvitation,
  getMyInvitations,
  invitationKeys,
} from './invitationsApi.ts';

/**
 * Invitations waiting for this account, shown at the top of the home screen.
 *
 * This is the in-app half of the flow and it is not optional: someone who deleted the
 * email, or never got it past a spam filter, still finds the invitation here.
 * Renders nothing when there is nothing waiting.
 */
export function PendingInvitations() {
  const queryClient = useQueryClient();

  const { data: invitations } = useQuery({
    queryKey: invitationKeys.mine,
    queryFn: getMyInvitations,
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: invitationKeys.mine }),
      queryClient.invalidateQueries({ queryKey: groupKeys.all }),
    ]);
  };

  const accept = useMutation({
    mutationFn: (invitationId: string) => acceptInvitation({ invitationId }),
    onSuccess: refresh,
  });

  const decline = useMutation({
    mutationFn: declineInvitation,
    onSuccess: refresh,
  });

  const actionable = invitations?.filter((invitation) => invitation.isActionable) ?? [];

  if (actionable.length === 0) {
    return null;
  }

  const error = accept.error ?? decline.error;

  return (
    <section className="card stack invitations">
      <h2>{actionable.length === 1 ? 'An invitation' : 'Invitations'}</h2>

      {error && (
        <p className="form-error" role="alert">
          {describeError(error)}
        </p>
      )}

      <ul className="invitation-list">
        {actionable.map((invitation) => (
          <li key={invitation.id} className="invitation">
            <p className="invitation__text">
              <strong>{invitation.invitedByDisplayName}</strong> invited you to{' '}
              <strong>{invitation.groupName}</strong>
            </p>

            <div className="invitation__actions">
              <button
                type="button"
                className="button button--primary button--small"
                onClick={() => accept.mutate(invitation.id)}
                disabled={accept.isPending || decline.isPending}
              >
                Join
              </button>
              <button
                type="button"
                className="button button--quiet button--small"
                onClick={() => decline.mutate(invitation.id)}
                disabled={accept.isPending || decline.isPending}
              >
                No thanks
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
